import React, { useState, Children } from 'react';
import { Home, TrendingUp, Lightbulb, Settings, ChevronRight, ArrowLeft, LogOut, CalendarDays, Users, FileText } from 'lucide-react';
import { DjayAvatar } from '../../mascot';
export type ParentV2Route = 'home' | 'voortgang' | 'toetsen' | 'tips' | 'activiteit' | 'compare' | 'settings';
interface NavItem {
  id: ParentV2Route;
  label: string;
  icon: React.ReactNode;
  /** Optional minimum number of linked children required for this item to render. */
  minChildren?: number;
}
const NAV_ITEMS: NavItem[] = [{
  id: 'home',
  label: 'Overzicht',
  icon: <Home className="w-5 h-5" data-id="element-7647" />
}, {
  id: 'voortgang',
  label: 'Vakken',
  icon: <TrendingUp className="w-5 h-5" data-id="element-7648" />
}, {
  id: 'toetsen',
  label: 'Toetsen',
  icon: <FileText className="w-5 h-5" data-id="element-7649" />
}, {
  id: 'tips',
  label: 'Tips',
  icon: <Lightbulb className="w-5 h-5" data-id="element-7650" />
}, {
  id: 'activiteit',
  label: 'Activiteit',
  icon: <CalendarDays className="w-5 h-5" data-id="element-7651" />
}, {
  id: 'compare',
  label: 'Vergelijken',
  icon: <Users className="w-5 h-5" data-id="element-7652" />,
  minChildren: 2
}];
interface ParentSidebarV2Props {
  activeRoute: ParentV2Route;
  onNavigate: (route: ParentV2Route) => void;
  onLogout?: () => void;
  onReturnHome?: () => void;
  parentName?: string;
  linkedLearnersCount?: number;
}
/**
 * Parent portal sidebar.
 *
 * Wave 1 (F-SHL-1): The child selector was removed from this sidebar.
 * The page-level `ChildSelectorBar` (pill row) is now the single source of
 * truth for multi-child switching. This avoids two controls competing for
 * the same state.
 */
export function ParentSidebarV2({
  activeRoute,
  onNavigate,
  onLogout,
  onReturnHome,
  parentName = 'Ouder',
  linkedLearnersCount = 0
}: ParentSidebarV2Props) {
  const [collapsed, setCollapsed] = useState(false);
  return <aside className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 flex-shrink-0 h-full ${collapsed ? 'w-20' : 'w-64'}`} data-id="element-7653">
      {/* Header / Logo */}
      <div className={`flex items-center border-b border-slate-100 flex-shrink-0 h-20 ${collapsed ? 'justify-center px-2' : 'justify-between px-6'}`} data-id="element-7654">
        {!collapsed && <div className="flex items-center gap-3" data-id="element-7655">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100 shadow-sm overflow-hidden" data-id="element-7656">
              <DjayAvatar size="xs" emotion="happy" data-id="element-7657" />
            </div>
            <div className="flex flex-col" data-id="element-7658">
              <span className="text-sm font-black text-slate-900 leading-tight tracking-tight" data-id="element-7659">
                Meester djay
              </span>
              <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-7660">
                Ouderportaal
              </span>
            </div>
          </div>}
        {collapsed && <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100 shadow-sm overflow-hidden" data-id="element-7661">
            <DjayAvatar size="xs" emotion="happy" data-id="element-7662" />
          </div>}
        {!collapsed && <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none" aria-label="Inklappen" data-id="element-7663">
            <ChevronRight className="w-4 h-4 rotate-180" data-id="element-7664" />
          </button>}
      </div>

      {/* Parent Info */}
      {!collapsed && <div className="px-6 py-4 border-b border-slate-100" data-id="element-7665">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-7666">
            Ingelogd als
          </div>
          <div className="text-sm font-bold text-slate-900 truncate" data-id="element-7667">
            {parentName}
          </div>
        </div>}

      {/* 0 Children CTA */}
      {!collapsed && linkedLearnersCount === 0 && <div className="px-4 pt-4" data-id="element-7668">
          <div className="p-4 rounded-xl border-2 border-dashed border-emerald-100 bg-emerald-50/50 flex flex-col items-center text-center" data-id="element-7669">
            <h3 className="text-sm font-bold text-slate-900 mb-1" data-id="element-7670">
              Nog geen kind gekoppeld
            </h3>
            <p className="text-xs text-slate-600 mb-3" data-id="element-7671">
              Koppel je kind om voortgang te zien.
            </p>
            <button onClick={() => onNavigate('settings')} className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-colors shadow-sm" data-id="element-7672">
              Kind koppelen
            </button>
          </div>
        </div>}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-6 px-3" data-id="element-7673">
        <div className="space-y-1.5" data-id="element-7674">
          {NAV_ITEMS.filter(item => !item.minChildren || linkedLearnersCount >= item.minChildren).map(item => {
          const isActive = activeRoute === item.id;
          return <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 relative focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none ${collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} ${isActive ? 'bg-emerald-50 text-emerald-700 font-bold shadow-sm border border-emerald-100/50' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium border border-transparent'}`} data-id="element-7675">
                <span className={`flex-shrink-0 transition-colors ${isActive ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-600'}`} data-id="element-7676">
                  {item.icon}
                </span>
                {!collapsed && <span className="text-sm truncate" data-id="element-7677">{item.label}</span>}
              </button>;
        })}
        </div>
      </nav>

      {/* Footer */}
      <div className={`flex-shrink-0 border-t border-slate-100 ${collapsed ? 'p-3' : 'p-5'} space-y-2`} data-id="element-7678">
        {collapsed && <button onClick={() => setCollapsed(false)} className="w-full flex items-center justify-center p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors mb-2" aria-label="Uitklappen" data-id="element-7679">
            <ChevronRight className="w-5 h-5" data-id="element-7680" />
          </button>}

        <button onClick={() => onNavigate('settings')} title={collapsed ? 'Instellingen' : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5'} ${activeRoute === 'settings' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 font-medium'}`} data-id="element-7681">
          <Settings className={`w-5 h-5 ${activeRoute === 'settings' ? 'text-emerald-600' : ''}`} data-id="element-7682" />
          {!collapsed && <span className="text-sm" data-id="element-7683">Instellingen</span>}
        </button>

        {onLogout && <button onClick={onLogout} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Uitloggen' : undefined} data-id="element-7684">
            <LogOut className="w-5 h-5" data-id="element-7685" />
            {!collapsed && <span className="text-sm" data-id="element-7686">Uitloggen</span>}
          </button>}

        {onReturnHome && <button onClick={onReturnHome} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Naar leerlingomgeving' : undefined} data-id="element-7687">
            <ArrowLeft className="w-5 h-5" data-id="element-7688" />
            {!collapsed && <span className="text-sm" data-id="element-7689">Leerlingomgeving</span>}
          </button>}
      </div>
    </aside>;
}